import apiClient from "./api";

const normalizeErrorDetail = (detail) => {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") return first.msg || JSON.stringify(first);
    return JSON.stringify(detail);
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

export async function getMySubscriptions() {
  try {
    const res = await apiClient.get("/subscriptions/me");
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to load subscriptions" };
  }
}

export async function createSubscription(payload) {
  try {
    const res = await apiClient.post("/subscriptions/", payload);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to create subscription" };
  }
}

export async function updateSubscription(id, payload) {
  try {
    const res = await apiClient.put(`/subscriptions/${id}`, payload);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to update subscription" };
  }
}

export async function pauseSubscription(id) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/pause`, null);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to pause subscription" };
  }
}

export async function resumeSubscription(id) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/resume`, null);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to resume subscription" };
  }
}

export async function skipNextDelivery(id) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/skip`, null);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to skip next delivery" };
  }
}

export async function pauseUntilDate(id, untilISO) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/pause-until`, null, { params: { until: untilISO } });
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to pause until date" };
  }
}

export async function getDeliveryPreview(id, limit = 3) {
  try {
    const res = await apiClient.get(`/subscriptions/${id}/preview`, { params: { limit } });
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to load delivery preview" };
  }
}

export async function cancelSubscription(id) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/cancel`, null);
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to cancel subscription" };
  }
}

export async function deleteSubscription(id) {
  try {
    await apiClient.delete(`/subscriptions/${id}`);
    return { success: true };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to delete subscription" };
  }
}

export async function uploadPrescription(file) {
  try {
    const form = new FormData();
    form.append("file", file);
    const res = await apiClient.post("/subscriptions/upload-prescription", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { success: true, url: res.data?.url };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to upload prescription" };
  }
}

export async function confirmSubscription(id, action, payment_method) {
  try {
    const res = await apiClient.patch(`/subscriptions/${id}/confirm`, { action, payment_method });
    return { success: true, data: res.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return { success: false, error: normalizeErrorDetail(detail) || "Failed to confirm subscription" };
  }
}

/** Products the signed-in user has bought at least ``minOrders`` units of (for subscription nudges). */
export async function getSubscriptionPurchaseSuggestions(minOrders = 3) {
  try {
    const res = await apiClient.get("/subscription-suggestions/me", {
      params: { min_orders: minOrders },
    });
    return { success: true, data: res.data || [] };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return {
      success: false,
      data: [],
      error: normalizeErrorDetail(detail) || "Failed to load suggestions",
    };
  }
}
